import React, { useState, useRef, useEffect, useCallback, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Grid,
  Stack,
  SxProps,
  Theme,
  useMediaQuery,
  BoxProps,
  Hidden
} from '@mui/material';
import { DayPicker, useNavigation } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import SuperModalRegistration, {
  AppliedPOSItem
} from './SuperModalRegistration';
import SellerRegDialog from './SellerRegDialog';
import { resetSubmitError } from '../../redux/slices/sellerRegSlice';
import { rules } from '../util/validateFormInput';
import { visuallyHidden } from '@mui/utils';

import {
  getLabelSx,
  getDescriptionSx,
  cardSx,
  sectionTitleSx,
  iconInputSx,
  footerSx,
  textFieldInputSx,
  textFieldUnderlineSx,
  textFieldErrorUnderlineSx,
  textFieldLabelSx,
  helperTextSx,
  renderValuePlaceholderStyle,
  renderValueSelectedStyle,
  screenReaderAlertSx,
  reviewRowLabelSx,
  reviewRowValueSx,
  reviewIconImageSx,
  fieldErrorTextSx,
  getCalendarNavButtonSx,
  calendarMonthTitleSx,
  calendarCloseButtonSx,
  calendarCaptionContainerSx,
  datePickerCalendarIconSx,
  getDatePickerArrowIndicatorSx,
  datePickerArrowImgSx,
  datePickerHelperTextSx,
  datePickerClearButtonSx,
  getDatePickerFooterSx,
  datePickerPopupSx,
  radioQuestionTextSx,
  radioSx,
  radioFormControlLabelSx,
  getSelectFloatingLabelSx,
  getSelectTriggerSx,
  getSelectArrowSx,
  dropdownCheckmarkSx,
  selectDropdownSx,
  getSelectOptionSx,
  posEditLinkButtonSx,
  posSelectLinkButtonSx,
  posPlusIconSx,
  posOverflowBadgeSx,
  posOverflowBadgeTextSx,
  posCountryNameSx,
  posCountryIataSx,
  getStep1PosLabelSx,
  step4PosLabelSx,
  fieldIconSx,
  adminCheckboxSx,
  fallbackTextSx,
  step4PosStackSx,
  reviewRowContainerSx,
  footerButtonSx,
  fieldErrorIconSx,
  fieldErrorStackSx,
  calendarCloseIconSx,
  getSvgIconSx
} from './SellerRegistrationForm.styles';

export interface SellerRegistrationFormData {
  // Step 1
  itTeam: string;
  apiSchema: string[];
  appliedPOS: AppliedPOSItem[];
  bizModel: string[];
  // Step 2
  sameAsBusinessContact: boolean;
  companyName: string;
  companyAddress: string;
  companyWebsite: string;
  bizFirstName: string;
  bizLastName: string;
  bizEmail: string;
  bizPhone: string;
  adminFirstName: string;
  adminLastName: string;
  adminEmail: string;
  adminPhone: string;
  customers: string;
  // Step 3
  ndcIntegration: string;
  ndcSpecify: string;
  flow: string;
  implDate: Date | null;
}

interface SellerRegistrationFormProps {
  stepNumber: number;
  onContinue: () => void;
  onBack?: () => void;
  scrollOffset?: number;
  formData: SellerRegistrationFormData;
  updateFormData: (updates: Partial<SellerRegistrationFormData>) => void;
  onOtherSelected?: () => void;
}

interface StepFormProps {
  onContinue: () => void;
  onBack?: () => void;
  formData: SellerRegistrationFormData;
  updateFormData: (updates: Partial<SellerRegistrationFormData>) => void;
  state?: any;
  language?: string;
  onOtherSelected?: () => void;
}
// ─── Typography description style function ─────────────────────────────────────
// ─── Typography label style function for 'same as business contact' ─────────────
// getLabelSx, getDescriptionSx, cardSx, sectionTitleSx, iconInputSx, footerSx,
// textFieldInputSx, textFieldUnderlineSx, textFieldLabelSx,
// renderValuePlaceholderStyle, renderValueSelectedStyle
// → all imported from ./test.styles

// ─── SvgIcon ──────────────────────────────────────────────────────────────────
//
// Renders an SVG from the public folder using CSS mask-image so the fill color
// can be controlled via the `color` prop. Works for any single-color SVG.
//
// Usage:
//   <SvgIcon src="/images/registration/errorIcon.svg" color="#D32F2F" size={16} />
//   <SvgIcon src="/images/registration/phoneIcon.svg" color="currentColor" size={20} alt="Phone" />

interface SvgIconProps extends BoxProps {
  /** Path to the SVG file (relative to public folder). */
  src: string;
  /** CSS color value. Defaults to "currentColor". */
  color?: string;
  /** Width and height in px. Defaults to 24. */
  size?: number;
  /** Override width independently. Falls back to `size`. */
  width?: number;
  /** Override height independently. Falls back to `size`. */
  height?: number;
  /** Accessible label. When omitted the icon is hidden from screen readers. */
  alt?: string;
  /** Additional MUI sx styles. */
  sx?: SxProps<Theme>;
  className?: string;
}

const SvgIcon: React.FC<SvgIconProps> = ({
  src,
  color = 'currentColor',
  size = 24,
  width,
  height,
  alt,
  sx,
  className,
  ...props
}) => {
  const w = width ?? size;
  const h = height ?? size;
  return (
    <Box
      role={alt ? 'img' : undefined}
      aria-label={alt || undefined}
      aria-hidden={!alt ? true : undefined}
      className={className}
      sx={getSvgIconSx(src, color, w, h, sx)}
      {...props}
    />
  );
};

// ─── Section title component ────────────────────────────────────────────────────

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Box component="h3" sx={sectionTitleSx}>
    {children}
  </Box>
);

interface ErrorSummaryProps {
  messages: string[];
  summaryRef?: React.RefObject<HTMLDivElement>;
}

const ErrorSummary = ({
  messages,
  summaryRef
}: ErrorSummaryProps) => {
  if (!messages.length) return null;

  return (
    <Box
      ref={summaryRef}
      role="alert"
      tabIndex={-1}
      sx={visuallyHidden
      }
    >
      <Typography>
        Please correct the following errors:
      </Typography>

      <ul>
        {messages.map((message, index) => (
          <li key={index}>{message}</li>
        ))}
      </ul>
    </Box>
  );
};

// ─── Visually-hidden live region — announces validation summary to screen readers ──

const ScreenReaderAlert = ({ message }: { message: string }) => (
  <Box role="alert" aria-atomic="true" sx={screenReaderAlertSx}>
    {message}
  </Box>
);

// ─── Review row — label + value stack used in Step 4 ───────────────────────────

const ReviewRow = ({
  label,
  value
}: {
  label: string;
  value: React.ReactNode;
}) => {
  const { t } = useTranslation();
  return (
    <Stack sx={reviewRowContainerSx}>
      <Typography sx={reviewRowLabelSx}>{label}</Typography>
      <Typography aria-labelledby={`aria-screen-${label}`} sx={reviewRowValueSx}>{value}   <Box component="span" sx={visuallyHidden}>{value === '—' && t('registrationStepsData.common.fallbackEmptyAriaLabel')}</Box>
      </Typography>
    </Stack>
  )
}
  ;

// ─── Review row with leading icon (Step 4) ────────────────────────────────────

interface ReviewIconRowProps {
  src: string;
  alt: string;
  label: string;
  value: React.ReactNode;
}

const ReviewIconRow = ({ src, alt, label, value }: ReviewIconRowProps) => (
  <Box sx={iconInputSx}>
    <SvgIcon src={src} color="#000000" size={24} alt={alt} />
    <ReviewRow label={label} value={value} />
  </Box>
);

// ─── Dropdown option sort helper ──────────────────────────────────────────────
//
// Sorts a language-keyed array from Redux state: 'All' first, 'Other' last,
// remaining entries alphabetically. Returns the mapped description strings.
// Comparisons use code (language-agnostic) instead of desc to handle French:
//   'All' / 'Tous'  → codes: 'ALL' (customerTypes), 'SF003' (supportedFlow)
//   'Other' / 'Autre' → code: 'AS001' (apiSchemas)

// ─── Dropdown option sort helper ──────────────────────────────────────────────
//
// Sorts a language-keyed array from Redux state by an explicit per-code display
// order, falling back to alphabetical for any code not listed. Comparisons use
// code (language-agnostic) so French labels order identically.
//
// To change/extend ordering, edit DROPDOWN_ORDER only — negative pins to top,
// large positive pins to bottom, absent codes sort alphabetically (priority 0).
// Shared (exported) so CompanyProfile ranks the same dropdowns identically.
//   All / Tous        → 'ALL' (customerTypes), 'SF003' (supportedFlow)  → top
//   Customer types    → 'CO' Corporate, 'CT001' Leisure, 'CT002' Both
//   Other / Autre     → 'BM002' (businessModel), 'AS001' (apiSchemas)   → bottom

export const DROPDOWN_ORDER: Record<string, number> = {
  ALL: -1,
  SF003: -1,
  CO: 1,
  CT001: 2,
  CT002: 3,
  BM002: 999,
  AS001: 999,
};

export const rankDropdownCode = (code: string): number =>
  DROPDOWN_ORDER[(code || '').toUpperCase()] ?? 0;

const sortDropdownOptions = (arr: any[] | undefined): string[] => {
  if (!Array.isArray(arr)) return [];
  return [...arr]
    .sort((a, b) => {
      const diff = rankDropdownCode(a.code) - rankDropdownCode(b.code);
      return diff !== 0
        ? diff
        : (a.desc || '').toLowerCase().localeCompare((b.desc || '').toLowerCase());
    })
    .map((item: any) => item.desc);
};

// ─── Validation ───────────────────────────────────────────────────────────────
//
// Three fully separated concerns:
//
//   1. ERROR_MESSAGES  — every user-facing string in one place.
//                        Translate, A/B-test, or rebrand by changing only this object.
//
//   2. VALIDATION_RULES — pure boolean predicates.
//                        Zero strings. Each rule references a key from ERROR_MESSAGES.
//                        Reuse rules across profiles without duplicating messages.
//
//   3. validate()       — thin orchestrator. Given a profile name, runs its rules
//                        in order and looks up the message for the first failure.
//
// Adding a new profile (e.g. 'companyEmail'):
//   - Add its rule-list to VALIDATION_RULES (reuse existing rule functions if possible)
//   - Add any new message strings to ERROR_MESSAGES
//   - ValidationType updates automatically — nothing else to change.

// ─── 1. Error messages ────────────────────────────────────────────────────────

// Values are i18n keys — call t(validate(...)) at usage sites.
export const ERROR_MESSAGES = {
  STRING_INVALID: 'registrationStepsData.validation.generic.stringInvalid',
  IATA_NUMBERS_ONLY: 'registrationStepsData.validation.iata.numbersOnly',
  IATA_MAX_LENGTH: 'registrationStepsData.validation.iata.maxLength',
  WEBSITE_INVALID: 'registrationStepsData.validation.website.invalid',
  PHONE_INVALID: 'registrationStepsData.validation.phone.invalid',
  EMAIL_FORMAT_INVALID: 'registrationStepsData.validation.email.formatInvalid',
  EMAIL_DOMAIN_BLOCKED: 'registrationStepsData.validation.email.domainBlocked',
  DECIMAL_INVALID: 'registrationStepsData.validation.decimal.invalid',
  SALES_MIX_SUM_INVALID: 'registrationStepsData.validation.salesMix.sumInvalid'
} as const;

type ErrorKey = keyof typeof ERROR_MESSAGES;

// ─── 2. Validation rules — pure boolean predicates ───────────────────────────
//
// Each rule: { key: ErrorKey, test: (v, v2, v3) => boolean }
//   test returns true  → rule passes
//   test returns false → rule fails; ERROR_MESSAGES[key] will be shown

type Rule = {
  key: ErrorKey;
  test: (v: string, v2: string, v3: string) => boolean;
};

// Named validation profiles — each is an ordered list of { key, test } pairs
export const VALIDATION_RULES: Record<string, Rule[]> = {
  // Generic string — blocks HTML/script injection
  string: [{ key: 'STRING_INVALID', test: (v) => rules.noHtml(v) }],

  // IATA — digits only, max 8 characters
  iata: [
    { key: 'IATA_NUMBERS_ONLY', test: (v) => rules.digitsOnly(v) },
    { key: 'IATA_MAX_LENGTH', test: (v) => rules.maxLength8(v) }
  ],

  // Website URL — optional field
  website: [{ key: 'WEBSITE_INVALID', test: (v) => rules.validUrl(v) }],

  // Phone — optional field
  phone: [{ key: 'PHONE_INVALID', test: (v) => rules.validPhone(v) }],

  // Business email — validates format AND blocks personal/free domains
  email: [
    { key: 'EMAIL_FORMAT_INVALID', test: (v) => rules.validEmailFmt(v) },
    { key: 'EMAIL_DOMAIN_BLOCKED', test: (v) => rules.noFreeEmail(v) }
  ],

  // Personal email — format only, no domain restriction
  personalEmail: [
    { key: 'EMAIL_FORMAT_INVALID', test: (v) => rules.validEmailFmt(v) }
  ],

  // Decimal 0–100 — for sales-mix percentage fields
  decimal: [
    { key: 'DECIMAL_INVALID', test: (v) => rules.validDecimalFmt(v) },
    { key: 'DECIMAL_INVALID', test: (v) => rules.decimalInRange(v) }
  ],

  // Sales mix sum — domestic + transborder + international must equal 100
  salesMixSum: [
    {
      key: 'SALES_MIX_SUM_INVALID',
      test: (v, v2, v3) => rules.salesMixEquals100(v, v2, v3)
    }
  ]
};

// ValidationType is derived from VALIDATION_RULES keys — no manual union to maintain
export type ValidationType = keyof typeof VALIDATION_RULES;

// ─── 3. validate() — orchestrator ────────────────────────────────────────────
//
// Runs rules for the given profile in order.
// Returns ERROR_MESSAGES[key] for the first failing rule, or '' if all pass.

export const validate = (
  type: ValidationType,
  value: string,
  value2 = '',
  value3 = ''
): string => {
  for (const rule of VALIDATION_RULES[type]) {
    if (!rule.test(value, value2, value3)) return ERROR_MESSAGES[rule.key];
  }
  return '';
};

// ─── Field error component ─────────────────────────────────────────────────────
// ─── Common Footer component ─────────────────────────────────────────────────────

interface CommonFooterProps {
  onBack?: () => void;
  onSubmit?: () => void;
  backText?: string;
  submitText?: string;
  submitVariant?: 'primary' | 'contained' | 'secondary';
  backVariant?: 'secondary' | 'outlined' | 'primary';
  submitDisabled?: boolean;
  sx?: SxProps<Theme>;
}

const CommonFooter = ({
  onBack,
  onSubmit,
  backText,
  submitText,
  submitVariant = 'primary',
  backVariant = 'secondary',
  submitDisabled = false,
  sx
}: CommonFooterProps) => {
  const { t } = useTranslation();
  const resolvedBackText =
    backText ?? t('registrationStepsData.common.buttons.back');
  const resolvedSubmitText =
    submitText ?? t('registrationStepsData.common.buttons.submit');
  return (
    <Box
      sx={
        [
          footerSx,
          sx,
          !onBack && {
            width: '100% !important',
            display: 'flex !important',
            flexDirection: 'row !important',
            justifyContent: 'flex-end !important',
            alignItems: 'center !important'
          }
        ] as SxProps<Theme>
      }
    >
      {onBack && (
        <Button
          variant={backVariant}
          size="md"
          onClick={onBack}
          sx={footerButtonSx}
        >
          {resolvedBackText}
        </Button>
      )}
      <Button
        variant={submitVariant}
        size="md"
        onClick={onSubmit}
        disabled={submitDisabled}
        sx={footerButtonSx}
      >
        {resolvedSubmitText}
      </Button>
    </Box>
  );
};

const FieldError = ({ message, id }: { message: string; id?: string }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={0.5}
    sx={fieldErrorStackSx}
  >
    <SvgIcon
      src="/images/registration/errorIcon.svg"
      size={16}
      color="var(--Sem-icon-colour-icon-error, #9E231B)"
      sx={fieldErrorIconSx}
    />
    <Typography id={id} sx={fieldErrorTextSx}>
      {message}
    </Typography>
  </Stack>
);

// ─── Calendar caption component ─────────────────────────────────────────────────────────

interface CalendarCaptionProps {
  displayMonth: Date;
  onClose: () => void;
  titleId?: string;
}

const CalendarCaption = ({
  displayMonth,
  onClose,
  titleId
}: CalendarCaptionProps) => {
  const { goToMonth, nextMonth, previousMonth } = useNavigation();
  const { t } = useTranslation();
  return (
    <Box sx={calendarCaptionContainerSx}>
      {/* Prev button */}
      <Box
        component="button"
        type="button"
        disabled={!previousMonth}
        onClick={() => previousMonth && goToMonth(previousMonth)}
        sx={getCalendarNavButtonSx(!previousMonth, 'left')}
        aria-label={t(
          'registrationStepsData.modal.calendar.previousMonthAriaLabel'
        )}
      >
        <SvgIcon
          src="/images/registration/dateArrow.svg"
          size={24}
          sx={{ transform: 'rotate(180deg)' }}
          color="#1460AA"
        />
      </Box>
      {/* Title */}
      <Typography id={titleId} sx={calendarMonthTitleSx}>
        {displayMonth.toLocaleDateString('en-US', {
          month: 'long',
          year: 'numeric'
        })}
      </Typography>
      {/* Next button */}
      <Box
        component="button"
        type="button"
        disabled={!nextMonth}
        onClick={() => nextMonth && goToMonth(nextMonth)}
        sx={getCalendarNavButtonSx(!nextMonth, 'right')}
        aria-label={t(
          'registrationStepsData.modal.calendar.nextMonthAriaLabel'
        )}
      >
        <SvgIcon
          src="/images/registration/dateArrow.svg"
          size={24}
          color="#1460AA"
        />
      </Box>
      {/* Close button */}
      <Box
        component="button"
        type="button"
        onClick={onClose}
        sx={calendarCloseButtonSx}
        aria-label={t('registrationStepsData.modal.calendar.closeAriaLabel')}
      >
        <SvgIcon
          src="/images/registration/closeButton.svg"
          size={16}
          sx={calendarCloseIconSx}
        />
      </Box>
    </Box>
  );
};

// ─── Common date picker component ────────────────────────────────────────────────────────

interface CommonDatePickerProps {
  label: string;
  value: Date | null;
  onChange: (value: Date | null) => void;
  helperText?: string;
  error?: boolean;
  errorMessage?: string;
  onOpenChange?: (open: boolean) => void;
}

const CommonDatePicker = ({
  label,
  value,
  onChange,
  helperText,
  error,
  errorMessage,
  onOpenChange
}: CommonDatePickerProps) => {
  const [open, setOpen] = useState(false);
  const [displayedMonth, setDisplayedMonth] = useState(() => new Date());
  const [pendingDate, setPendingDate] = useState<Date | null>(value);
  const containerRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLInputElement>(null);
  const wasOpenRef = useRef(false);
  const todayRef = useRef(new Date());
  const errorId = useId();
  const captionTitleId = "date-title"
  const { t } = useTranslation();

  const handleSetOpen = useCallback(
    (val: boolean) => {
      setOpen(val);
      onOpenChange?.(val);
    },
    [onOpenChange]
  );

  const formatted = value
    ? value.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    })
    : '';

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        handleSetOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [open, handleSetOpen]);

  // Return focus to the date input when the picker closes
  useEffect(() => {
    const shouldFocus = wasOpenRef.current && !open;
    wasOpenRef.current = open;
    if (!shouldFocus) return;
    const id = requestAnimationFrame(() => triggerRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open]);

  // Close picker on Escape (capture phase so DayPicker doesn't swallow the event)
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        handleSetOpen(false);
      }
    };
    if (open) document.addEventListener('keydown', handleEscape, true);
    return () => document.removeEventListener('keydown', handleEscape, true);
  }, [open, handleSetOpen]);

  // Trap Tab focus within the calendar dialog. Because focus starts on a day in
  // the middle of the DOM, a plain Tab would leave the dialog and skip the
  // prev/next month buttons that precede the grid — so we cycle focus here.
  useEffect(() => {
    if (!open) return;
    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const popup = popupRef.current;
      if (!popup) return;
      const focusable = Array.from(
        popup.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => el.offsetParent !== null);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        if (active === first || !popup.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last || !popup.contains(active)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleTab, true);
    return () => document.removeEventListener('keydown', handleTab, true);
  }, [open]);

  // Move focus into the calendar dialog when it opens so screen readers announce it
  // On open, move focus to the selected day (or today when nothing is selected).
  // DayPicker assigns tabIndex=0 to that day, so we focus it directly instead of
  // using `initialFocus` — that keeps the natural tab order (prev/next/close →
  // days → footer) so the nav buttons stay reachable with Tab.
  useEffect(() => {
    if (!open || !popupRef.current) return;
    const id = requestAnimationFrame(() => {
      const popup = popupRef.current;
      if (!popup) return;
      const focusTarget =
        popup.querySelector<HTMLElement>('.rdp-day[tabindex="0"]') ??
        popup.querySelector<HTMLElement>('.rdp-cell [tabindex="0"]') ??
        popup;
      focusTarget.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [open]);

  // Sync the displayed month and pending selection whenever the picker opens
  useEffect(() => {
    if (open) {
      setPendingDate(value);
      setDisplayedMonth(value ?? new Date());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Associate the visible month/year heading with the calendar grid so screen
  // readers announce it as the table's name, e.g. "July 2026, table with 6 rows
  // and 7 columns". Runs after paint so DayPicker's table is in the DOM.
  useEffect(() => {
    if (!open || !popupRef.current) return;
    const id = requestAnimationFrame(() => {
      const grid = popupRef.current?.querySelector('table');
      grid?.setAttribute('aria-labelledby', captionTitleId);
    });
    return () => cancelAnimationFrame(id);
  }, [open, displayedMonth, captionTitleId]);

  return (
    <Box ref={containerRef}>
      <Stack spacing={0.5}>
        <Box sx={{ position: 'relative' }}>
          <TextField
            label={label}
            variant="standard"
            fullWidth
            value={formatted}
            inputRef={triggerRef}
            InputLabelProps={{ shrink: open || !!value }}
            InputProps={{
              readOnly: true,
              startAdornment: (
                <InputAdornment position="start">
                  <SvgIcon
                    src="/images/registration/Calendar.svg"
                    size={20}
                    sx={datePickerCalendarIconSx}
                    color="#1460AA"
                  />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Box sx={getDatePickerArrowIndicatorSx(open)}>
                    <SvgIcon
                      src="/images/registration/dropdownArrowDown.png"
                      width={10}
                      height={5}
                      sx={datePickerArrowImgSx}
                      color="black"
                    />
                  </Box>
                </InputAdornment>
              )
            }}
            inputProps={{
              role: 'combobox',
              'aria-expanded': open,
              'aria-haspopup': 'dialog',
              'aria-invalid': error ? true : undefined,
              'aria-describedby': error && errorMessage ? errorId : undefined
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleSetOpen(!open);
              }
            }}
            onClick={() => handleSetOpen(!open)}
            sx={{
              '& label': {
                ...textFieldLabelSx,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                '&:not(.MuiInputLabel-shrink)': {
                  transform: 'translate(28px, 20px) scale(1)',
                  maxWidth: 'calc(100% - 50px)'
                }
              },
              '& input': { ...textFieldInputSx, cursor: 'pointer' },
              ...textFieldUnderlineSx
            }}
          />
          {open && (
            <Box
              ref={popupRef}
              role="dialog"
              aria-modal="true"
              aria-label={t(
                'registrationStepsData.modal.calendar.dialogAriaLabel',
                {
                  label,
                  defaultValue: '{{label}}, date picker'
                }
              )}
              className="day-picker"
              tabIndex={-1}
              sx={datePickerPopupSx}
            >
              <DayPicker
                mode="single"
                defaultMonth={pendingDate ?? todayRef.current}
                fromDate={todayRef.current}
                selected={pendingDate ?? undefined}
                onMonthChange={setDisplayedMonth}
                formatters={{
                  formatWeekdayName: (day) => {
                    const s = day
                      .toLocaleDateString('en-US', { weekday: 'short' })
                      .slice(0, 3);
                    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
                  }
                }}
                onSelect={(date) => {
                  if (date) setPendingDate(date);
                }}
                components={{
                  Caption: ({ displayMonth }) => (
                    <CalendarCaption
                      displayMonth={displayMonth}
                      titleId={captionTitleId}
                      onClose={() => handleSetOpen(false)}
                    />
                  )
                }}
              />
              <Box sx={getDatePickerFooterSx(!!value)}>
                {value && (
                  <Button
                    variant="text"
                    sx={datePickerClearButtonSx}
                    onClick={() => {
                      setPendingDate(null);
                      onChange(null);
                      handleSetOpen(false);
                    }}
                  >
                    {t('registrationStepsData.common.buttons.clearDate')}
                  </Button>
                )}
                <Button
                  onClick={() => {
                    onChange(pendingDate);
                    handleSetOpen(false);
                  }}
                  variant="primary"
                  size="md"
                >
                  {t('registrationStepsData.common.buttons.select')}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
        {error && errorMessage && (
          <FieldError id={errorId} message={errorMessage} />
        )}
        {!error && helperText && (
          <Typography sx={datePickerHelperTextSx}>{helperText}</Typography>
        )}
      </Stack>
    </Box>
  );
};

// ─── Common radio question component ────────────────────────────────────────────────────────

interface CommonRadioQuestionProps {
  name: string;
  question: string;
  value: string;
  onChange: (value: string) => void;
  options?: string[];
  optionLabels?: string[];
  error?: boolean;
  errorMessage?: string;
  inputRef?: React.Ref<HTMLInputElement>
}

const CommonRadioQuestion = ({
  name,
  question,
  value,
  onChange,
  options = ['Yes', 'No'],
  optionLabels,
  error,
  errorMessage,
  inputRef
}: CommonRadioQuestionProps) => {
  const questionId = useId();
  const errorId = useId();
  return (
    <Stack spacing={1}>
      <Stack spacing={0.5}>
        <Typography id={questionId} sx={radioQuestionTextSx}>
          {question}{'\u2060'}<span style={{ color: '#FF333A' }}>*</span>
        </Typography>
        {error && errorMessage && (
          <FieldError id={errorId} message={errorMessage} />
        )}
      </Stack>
      <RadioGroup
        row
        name={name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-labelledby={questionId}
        aria-describedby={error && errorMessage ? errorId : undefined}
        sx={{ gap: '16px' }}
      >
        {options.map((opt, i) => (
          <FormControlLabel
            key={opt}
            value={opt}
            control={<Radio inputRef={i === 0 ? inputRef : undefined} sx={radioSx} />}
            label={optionLabels?.[i] ?? opt}
            sx={radioFormControlLabelSx}
          />
        ))}
      </RadioGroup>
    </Stack>
  );
};

// ─── Grid layout helpers ──────────────────────────────────────────────────────

interface TwoColGridProps {
  children: React.ReactNode;
  itemProps?: Record<string, any>;
}

const TwoColGrid = ({ children, itemProps }: TwoColGridProps) => (
  <Grid container spacing={{ xs: 4, md: 2 }}>
    {React.Children.map(children, (child: React.ReactNode) => (
      <Grid item xs={12} sm={6} {...itemProps}>
        {child}
      </Grid>
    ))}
  </Grid>
);

// ─── Common TextField component ───────────────────────────────────────────────

const CommonTextField = ({
  label,
  disableUnderline = false,
  helperText,
  startIcon,
  value,
  onChange,
  onFocus,
  onBlur,
  error,
  errorMessage,
  ariaLabel,
  autoComplete,
  required,
  disabled,
  inputRef
}: {
  label: string;
  disableUnderline?: boolean;
  helperText?: string;
  startIcon?: React.ReactNode;
  value?: string;
  onChange?: (val: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  error?: boolean;
  required?: boolean;
  errorMessage?: string;
  ariaLabel?: string;
  autoComplete?: string;
  disabled?: boolean;
  inputRef?: React.Ref<HTMLInputElement>
}) => {
  const [focused, setFocused] = useState(false);
  const [internalHasValue, setInternalHasValue] = useState(false);
  const errorId = useId();
  const isControlled = value !== undefined;
  const hasValue = isControlled ? value.length > 0 : internalHasValue;

  return (
    <Box>
      <TextField
        required={required ? true : false}
        label={label}
        variant="standard"
        inputRef={inputRef}
        fullWidth
        disabled={disabled}
        value={isControlled ? value : undefined}
        onChange={(e) => {
          if (isControlled) {
            onChange?.(e.target.value);
          } else {
            setInternalHasValue(e.target.value.length > 0);
          }
        }}
        InputLabelProps={{ shrink: focused || hasValue }}
        InputProps={{
          disableUnderline,
          onFocus: () => {
            setFocused(true);
            onFocus?.();
          },
          onBlur: () => {
            setFocused(false);
            onBlur?.();
          },
          ...(startIcon && {
            startAdornment: (
              <InputAdornment position="start">{startIcon}</InputAdornment>
            )
          })
        }}
        inputProps={{
          'aria-label': ariaLabel || undefined,
          'aria-invalid': error ? true : undefined,
          'aria-describedby': error && errorMessage ? errorId : undefined,
          ...(autoComplete && { autoComplete })
        }}
        helperText={!error ? helperText : null}
        sx={{
          '& label': {
            ...textFieldLabelSx,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          },
          '& input': textFieldInputSx,
          ...textFieldUnderlineSx,
          ...(error && textFieldErrorUnderlineSx),
          ...(startIcon && {
            '& label:not(.MuiInputLabel-shrink)': {
              transform: 'translate(34px, 20px) scale(1)',
              maxWidth: 'calc(100% - 50px)'
            }
          }),
          ...helperTextSx
        }}
      />
      {error && errorMessage && (
        <FieldError id={errorId} message={errorMessage} />
      )}
    </Box>
  );
};

interface CommonSelectProps {
  value: string;
  onChange: (value: string) => void;
  menuList: string[];
  placeholder?: string;
  label?: string;
  error?: boolean;
  errorMessage?: string;
  required?: boolean;
  triggerRef?: React.Ref<HTMLDivElement>
}

const CommonSelect = ({
  value,
  onChange,
  menuList,
  placeholder,
  label,
  error,
  errorMessage,
  required,
  triggerRef
}: CommonSelectProps) => {
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const errorId = useId();
  const labelId = useId();
  const listboxId = useId();
  const optionIdBase = useId();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);
  const isActive = open || focused || !!value;

  const openDropdown = useCallback(() => {
    const idx = value ? menuList.indexOf(value) : -1;
    setActiveIndex(idx >= 0 ? idx : -1);
    setOpen(true);
  }, [value, menuList]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleOutside = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  // Scroll active option into view when navigating with keyboard
  useEffect(() => {
    if (!open || activeIndex < 0 || !listboxRef.current) return;
    const options =
      listboxRef.current.querySelectorAll<HTMLElement>('[role="option"]');
    options[activeIndex]?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open]);

  return (
    <Box
      ref={wrapperRef}
      sx={{ width: '100%', position: 'relative', mt: { xs: 1 } }}
    >
      {/* Floating Label — sits inside trigger (placeholder) until active */}
      {label && (
        <>
          <Typography id={labelId} sx={getSelectFloatingLabelSx(isActive)}>
            {label}
            <span style={{ color: '#FF333A' }}> {required ? '*' : null}</span>
          </Typography>
        </>
      )}

      {/* Trigger */}
      <Box
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-labelledby={label ? labelId : undefined}
        aria-activedescendant={
          open && activeIndex >= 0
            ? `${optionIdBase}-${activeIndex}`
            : undefined
        }
        aria-invalid={error ? true : undefined}
        aria-required={required || undefined}
        aria-describedby={error && errorMessage ? errorId : undefined}
        ref={triggerRef}
        tabIndex={0}
        onClick={() => {
          open ? setOpen(false) : openDropdown();
        }}
        onFocus={() => setFocused(true)}
        onBlur={(e: React.FocusEvent) => {
          setFocused(false);
          // Close when focus leaves the entire widget (not when clicking an option)
          if (!wrapperRef.current?.contains(e.relatedTarget as Node)) {
            setOpen(false);
          }
        }}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (!open) {
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
              e.preventDefault();
              openDropdown();
            }
          } else {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setActiveIndex((i) => Math.min(i + 1, menuList.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActiveIndex((i) => Math.max(i - 1, 0));
            } else if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              if (activeIndex >= 0 && activeIndex < menuList.length) {
                onChange(menuList[activeIndex]);
                setOpen(false);
              }
            } else if (e.key === 'Home') {
              e.preventDefault();
              setActiveIndex(0);
            } else if (e.key === 'End') {
              e.preventDefault();
              setActiveIndex(menuList.length - 1);
            } else if (e.key === 'Escape' || e.key === 'Tab') {
              setOpen(false);
            }
          }
        }}
        sx={getSelectTriggerSx(!!error)}
      >
        {value ? (
          <span style={renderValueSelectedStyle}>{value}</span>
        ) : !label && placeholder ? (
          <Typography sx={fallbackTextSx}>{placeholder}</Typography>
        ) : (
          <span />
        )}
        {/* Arrow */}
        <Box sx={getSelectArrowSx(open)} />
      </Box>

      {/* Dropdown — position: absolute, never causes scroll */}
      {open && (
        <Box
          ref={listboxRef}
          role="listbox"
          id={listboxId}
          aria-labelledby={label ? labelId : undefined}
          onMouseDown={(e) => e.preventDefault()}
          sx={selectDropdownSx}
        >
          {menuList.map((opt, i) => (
            <Box
              key={opt}
              id={`${optionIdBase}-${i}`}
              role="option"
              aria-selected={value === opt}
              onClick={() => {
                onChange(opt);
                setOpen(false);
              }}
              sx={getSelectOptionSx(value === opt, i === activeIndex)}
            >
              {opt}
              {value === opt && (
                <SvgIcon
                  src="/images/registration/checkmarkDropdown.svg"
                  size={20}
                  sx={dropdownCheckmarkSx}
                />
              )}
            </Box>
          ))}
        </Box>
      )}

      {error && errorMessage && (
        <FieldError id={errorId} message={errorMessage} />
      )}
    </Box>
  );
};

interface CommonMultiSelectProps {
  values: string[];
  onChange: (values: string[]) => void;
  menuList: string[];
  placeholder?: string;
  label?: string;
  error?: boolean;
  errorMessage?: string;
  required?: boolean;
  triggerRef?: React.Ref<HTMLDivElement>
}

const CommonMultiSelect = ({
  values,
  onChange,
  menuList,
  placeholder,
  label,
  error,
  errorMessage,
  required,
  triggerRef
}: CommonMultiSelectProps) => {
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const errorId = useId();
  const labelId = useId();
  const listboxId = useId();
  const optionIdBase = useId();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const listboxRef = useRef<HTMLDivElement>(null);
  const isActive = open || focused || values.length > 0;

  const openDropdown = useCallback(() => {
    const firstSelected = values.length > 0 ? menuList.indexOf(values[0]) : -1;
    setActiveIndex(firstSelected >= 0 ? firstSelected : -1);
    setOpen(true);
  }, [values, menuList]);

  const toggleOption = useCallback(
    (opt: string) => {
      if (values.includes(opt)) {
        onChange(values.filter((v) => v !== opt));
      } else {
        onChange([...values, opt]);
      }
    },
    [values, onChange]
  );

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleOutside = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  // Scroll active option into view when navigating with keyboard
  useEffect(() => {
    if (!open || activeIndex < 0 || !listboxRef.current) return;
    const options =
      listboxRef.current.querySelectorAll<HTMLElement>('[role="option"]');
    options[activeIndex]?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open]);

  const displayValue = values.length > 0 ? values.join(', ') : '';

  return (
    <Box
      ref={wrapperRef}
      sx={{ width: '100%', position: 'relative', mt: { xs: 1 } }}
    >
      {/* Floating Label */}
      {label && (
        <Typography id={labelId} sx={getSelectFloatingLabelSx(isActive)}>
          {label}
          <span style={{ color: '#FF333A' }}>{required ? '*' : null}</span>
        </Typography>
      )}

      {/* Trigger */}
      <Box
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-labelledby={label ? labelId : undefined}
        aria-activedescendant={
          open && activeIndex >= 0
            ? `${optionIdBase}-${activeIndex}`
            : undefined
        }
        aria-invalid={error ? true : undefined}
        aria-required={required || undefined}
        aria-describedby={error && errorMessage ? errorId : undefined}
        tabIndex={0}
        ref={triggerRef}
        onClick={() => {
          open ? setOpen(false) : openDropdown();
        }}
        onFocus={() => setFocused(true)}
        onBlur={(e: React.FocusEvent) => {
          setFocused(false);
          if (!wrapperRef.current?.contains(e.relatedTarget as Node)) {
            setOpen(false);
          }
        }}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (!open) {
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
              e.preventDefault();
              openDropdown();
            }
          } else {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setActiveIndex((i) => Math.min(i + 1, menuList.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActiveIndex((i) => Math.max(i - 1, 0));
            } else if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              if (activeIndex >= 0 && activeIndex < menuList.length) {
                toggleOption(menuList[activeIndex]);
              }
            } else if (e.key === 'Home') {
              e.preventDefault();
              setActiveIndex(0);
            } else if (e.key === 'End') {
              e.preventDefault();
              setActiveIndex(menuList.length - 1);
            } else if (e.key === 'Escape' || e.key === 'Tab') {
              setOpen(false);
            }
          }
        }}
        sx={getSelectTriggerSx(!!error)}
      >
        {displayValue ? (
          <span style={renderValueSelectedStyle}>{displayValue}</span>
        ) : !label && placeholder ? (
          <Typography sx={fallbackTextSx}>{placeholder}</Typography>
        ) : (
          <span />
        )}
        {/* Arrow */}
        <Box sx={getSelectArrowSx(open)} />
      </Box>

      {/* Dropdown */}
      {open && (
        <Box
          ref={listboxRef}
          role="listbox"
          id={listboxId}
          aria-multiselectable="true"
          aria-labelledby={label ? labelId : undefined}
          onMouseDown={(e) => e.preventDefault()}
          sx={selectDropdownSx}
        >
          {menuList.map((opt, i) => (
            <Box
              key={opt}
              id={`${optionIdBase}-${i}`}
              role="option"
              aria-selected={values.includes(opt)}
              onClick={() => toggleOption(opt)}
              sx={getSelectOptionSx(values.includes(opt), i === activeIndex)}
            >
              {opt}
              {values.includes(opt) && (
                <SvgIcon
                  src="/images/registration/checkmarkDropdown.svg"
                  size={20}
                  sx={dropdownCheckmarkSx}
                />
              )}
            </Box>
          ))}
        </Box>
      )}

      {error && errorMessage && (
        <FieldError id={errorId} message={errorMessage} />
      )}
    </Box>
  );
};

// ─── Reusable POS country chip (flag + name + IATA) ─────────────────────────
// Used in SelectedPOSSummary (Step 1) and the review section (Step 4).

const PosCountryItem = ({
  flag,
  name,
  iata
}: {
  flag?: string;
  name: string;
  iata?: string;
}) => (
  <Stack direction="row" alignItems="center" gap={1}>
    {flag && (
      <Box
        component="img"
        src={flag}
        alt=""
        sx={{ width: 24, height: 14, objectFit: 'contain', display: 'block' }}
      />
    )}
    <Box>
      <Typography sx={posCountryNameSx}>{name}</Typography>
      {iata && <Typography sx={posCountryIataSx}>{iata}</Typography>}
    </Box>
  </Stack>
);

// ─── SelectedPOSSummary sub-component ────────────────────────────────────────

const MAX_VISIBLE_POS = 4;

const SelectedPOSSummary = ({
  items,
  onEdit,
  editButtonRef
}: {
  items: AppliedPOSItem[];
  onEdit: () => void;
  editButtonRef?: React.Ref<HTMLButtonElement>;
}) => {
  const isMobile = useMediaQuery('(max-width:600px)');
  const maxVisible = isMobile ? 2 : MAX_VISIBLE_POS;
  const visible = items.slice(0, maxVisible);
  const overflow = items.length - maxVisible;
  const { t } = useTranslation();
  return (
    <Stack gap={3}>
      {/* Edit POS link */}
      <Stack direction="row" alignItems="center" gap={1}>
        <SvgIcon
          src="/images/registration/editIcon.svg"
          size={16}
          color="#1460AA"
        />
        <Button
          ref={editButtonRef}
          variant="text"
          onClick={onEdit}
          aria-haspopup="dialog"
          sx={posEditLinkButtonSx}
        >
          {t('registrationStepsData.form.step1.question3.editLink')}
        </Button>
      </Stack>

      {/* Country chips row */}
      <Stack
        direction="row"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
      >
        {visible.map((item) => (
          <Stack
            key={item.code}
            direction="row"
            alignItems="center"
            gap={1}
            px={{ md: 1.5 }}
          >
            <PosCountryItem
              flag={item.flag}
              name={item.code === 'OT' ? t('registrationStepsData.modal.othersRegion.formLabel') : item.name}
              iata={item.iata}
            />
          </Stack>
        ))}
        {overflow > 0 && (
          <Box
            role="img"
            aria-label={t('registrationStepsData.common.pos.andMore', {
              count: overflow
            })}
            sx={posOverflowBadgeSx}
          >
            <Typography sx={posOverflowBadgeTextSx}>+{overflow}</Typography>
          </Box>
        )}
      </Stack>
    </Stack>
  );
};

// ─── Step 1 form ──────────────────────────────────────────────────────────────

const Step1Form = ({
  onContinue,
  formData,
  updateFormData,
  state,
  language,
  onOtherSelected
}: StepFormProps) => {
  const { t } = useTranslation();
  const lang = language || 'en';
  const [posModalOpen, setPosModalOpen] = useState(false);
  const bizModelRef = useRef<HTMLDivElement>(null);       // CommonMultiSelect trigger
  const customersRef = useRef<HTMLDivElement>(null);      // CommonSelect trigger
  const editBtnRef = useRef<HTMLButtonElement>(null);
  const itTeamRef = useRef<HTMLInputElement>(null);
  const apiSchemaRef = useRef<HTMLDivElement>(null);
  const [posAnnouncement, setPosAnnouncement] = useState('');
  const [errors, setErrors] = useState({
    bizModel: false,
    customers: false,
    itTeam: false,
    apiSchema: false,
    pos: false
  });
  const [validationSummary, setValidationSummary] = useState('');
  const isPosDisabled = formData.bizModel.length === 0;
  // Language-safe Travel Agency detection: resolve the code from the English list,
  // then match it against the selected models' codes.
  const travelAgencyCode =
    (state.businessModel?.en ?? []).find(
      (i: any) => i.desc?.trim().toLowerCase() === 'travel agency'
    )?.code ?? '';
  const getBizModelCode = (desc: string): string =>
    (state.businessModel?.[lang] ?? []).find((i: any) => i.desc === desc)?.code ??
    '';
  const hasTravelAgency = (models: string[]): boolean =>
    !!travelAgencyCode &&
    models.some((desc) => getBizModelCode(desc) === travelAgencyCode);
  const isTravelAgency = hasTravelAgency(formData.bizModel);
  const handleContinue = () => {
    // ── Early-exit condition ─────────────────────────────────────────────────
    // If the user answered 'No' to Q1, skip the rest of the form and open the
    // plain screen.
    if (formData.itTeam === 'No') {
      onOtherSelected?.();
      return;
    }

    // ── Normal validation ─────────────────────────────────────────────────
    const newErrors = {
      bizModel: formData.bizModel.length === 0,
      customers: !formData.customers,
      itTeam: !formData.itTeam,
      apiSchema: !formData.apiSchema || formData.apiSchema.length === 0,
      pos: !formData.appliedPOS || formData.appliedPOS.length === 0
    };
    setErrors(newErrors);

    const focusOrder: Array<{ hasError: boolean; ref: React.RefObject<HTMLElement> }> = [
      { hasError: newErrors.bizModel, ref: bizModelRef },
      { hasError: newErrors.customers, ref: customersRef },
      { hasError: newErrors.itTeam, ref: itTeamRef },
      { hasError: newErrors.apiSchema, ref: apiSchemaRef },
      { hasError: newErrors.pos, ref: editBtnRef }
    ];
    const errorCount = Object.values(newErrors).filter(Boolean).length;
    if (errorCount > 0) {
      setValidationSummary(
        (errorCount === 1
          ? t('registrationStepsData.validation.summary.singular', {
            count: errorCount
          })
          : t('registrationStepsData.validation.summary.plural', {
            count: errorCount
          })) as string
      );
      const target = focusOrder.find(e => e.hasError)?.ref
      setTimeout(() => {
        target?.current?.focus();
        target?.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }, 100);

    } else {
      onContinue();
    }
  };

  // Language-aware, pre-sorted dropdown options for apiSchemas
  const apiSchemaOptions = sortDropdownOptions(state.apiSchemas?.[lang]);

  return (
    <Box sx={{ ...cardSx, mt: { xs: 3, md: 2 } }}>
      {/* Validation summary for screen readers */}
      <ScreenReaderAlert message={validationSummary} />
      <CommonMultiSelect
        label={t(
          'registrationStepsData.form.step2.businessModel.model.label'
        )}
        values={formData.bizModel}
        triggerRef={bizModelRef}
        onChange={(vals) => {
          const updates: Partial<SellerRegistrationFormData> = { bizModel: vals };
          if (vals.length === 0) {
            // No business model selected — POS section becomes disabled, so clear any selected countries/IATA
            updates.appliedPOS = [];
          } else if (!hasTravelAgency(vals) && formData.appliedPOS?.some((p) => p.iata)) {
            // Edge case: dropping Travel Agency clears any IATA numbers already entered
            updates.appliedPOS = formData.appliedPOS.map((p) => ({
              ...p,
              iata: ''
            }));
          }
          updateFormData(updates);
          setErrors((prev) => ({ ...prev, bizModel: false }));
        }}
        menuList={sortDropdownOptions(
          state.businessModel?.[lang]
        )}
        error={!!errors.bizModel}
        errorMessage={errors.bizModel ? t('registrationStepsData.form.step2.businessModel.model.errorRequired') : ''}
        required
      />
      <CommonSelect
        label={t(
          'registrationStepsData.form.step2.businessModel.customers.label'
        )}
        value={formData.customers}
        triggerRef={customersRef}
        onChange={(v) => {
          updateFormData({ customers: v });
          setErrors((prev) => ({...prev,  customers: false}))
        }}
        menuList={sortDropdownOptions(
          state.customerTypes?.[language || 'en']
        )}
        error={errors.customers}
        errorMessage={errors.customers? t('registrationStepsData.form.step2.businessModel.customers.errorRequired') : ''}
        required={true}
      />
      
      {/* Question 1 */}
      <Stack spacing={3}>
        <CommonRadioQuestion
          name="itTeam"
          question={t('registrationStepsData.form.step1.question1.label')}
          value={formData.itTeam}
          onChange={(val) => {
            updateFormData({ itTeam: val });
            setErrors((prev) => ({ ...prev, itTeam: false }));
          }}
          optionLabels={[
            t('registrationStepsData.common.yes'),
            t('registrationStepsData.common.no')
          ]}
          error={errors.itTeam}
          inputRef={itTeamRef}
          errorMessage={t('registrationStepsData.form.step1.question1.error')}
        />
      </Stack>

      {/* Question 2 */}
      <Stack
        spacing={0.5}
        sx={{ width : { xs: '100%' } }}
      >
        <CommonMultiSelect
          label={t('registrationStepsData.form.step1.question2.label')}
          values={formData.apiSchema}
          onChange={(val) => {
            updateFormData({ apiSchema: val });
            setErrors((prev) => ({ ...prev, apiSchema: false }));
          }}
          menuList={apiSchemaOptions}
          error={errors.apiSchema}
          errorMessage={t('registrationStepsData.form.step1.question2.error')}
          required={true}
          triggerRef={apiSchemaRef}
        />
      </Stack>

      {/* Question 3 */}
      <Stack spacing={1}>
        <Stack spacing={0.5}>
          <Typography sx={getStep1PosLabelSx(errors.pos, !isPosDisabled)}>
            {t('registrationStepsData.form.step1.question3.label')}
            <span style={{ color: '#FF333A' }}>*</span>
          </Typography>
          {errors.pos && (
            <FieldError
              message={t('registrationStepsData.form.step1.question3.error')}
            />
          )}
        </Stack>
        {formData.appliedPOS && formData.appliedPOS.length > 0 ? (
          <SelectedPOSSummary
            items={formData.appliedPOS}
            onEdit={() => setPosModalOpen(true)}
            editButtonRef={editBtnRef}
          />
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            sx={{ gap: { xs: '8px', sm: '10px' } }}
          >
            <SvgIcon
              src="/images/registration/posPlus.svg"
              size={16}
              sx={posPlusIconSx}
              color={isPosDisabled ? 'var(--Sem-icon-colour-icon-disabled, #B7C9D9)' : 'var(--Sem-icon-colour-icon-primary, #1460AA)'}
            />
            <Button
              variant="text"
              size="sm"
              sx={posSelectLinkButtonSx}
              aria-haspopup="dialog"
              onClick={() => setPosModalOpen(true)}
              ref={editBtnRef}
              disabled={isPosDisabled}
            >
              {t('registrationStepsData.form.step1.question3.selectLink')}
            </Button>
          </Stack>
        )}
        {/* Live region: updated after POS selection — announces summary to SR */}
        <Box
          id="pos-selected-summary"
          aria-live="polite"
          aria-atomic="true"
          sx={screenReaderAlertSx}
        >
          {posAnnouncement}
        </Box>

        <SuperModalRegistration
          open={posModalOpen}
          collectIata={isTravelAgency}
          onClose={() => {setPosModalOpen(false); requestAnimationFrame(() => {
            editBtnRef.current?.focus();
          });}}
          onOtherSelected={() => {
            setPosModalOpen(false);
            onOtherSelected?.();
          }}
          onNext={(items) => {
            updateFormData({ appliedPOS: items });
            setErrors((prev) => ({ ...prev, pos: false }));
            setPosModalOpen(false);
            // Clear first so the next set is always seen as a change by the live region
            setPosAnnouncement('');
            requestAnimationFrame(() => {
              editBtnRef.current?.focus();
              // Defer announcement to next frame so SR processes focus first
              requestAnimationFrame(() => {
                setPosAnnouncement(
                  t('registrationStepsData.form.step1.question3.selectedSummary', {
                    count: items.length,
                    countries: items.map((p) => p.name).join(', ')
                  }) as string
                );
              });
            });
          }}
          initialSelected={
            formData.appliedPOS ? formData.appliedPOS.map((p) => p.code) : []
          }
          initialIataValues={
            formData.appliedPOS
              ? Object.fromEntries(
                formData.appliedPOS.map((p) => [p.code, p.iata])
              )
              : {}
          }
        />
      </Stack>

      {/* Continue */}
      <CommonFooter
        onSubmit={handleContinue}
        submitText={t('registrationStepsData.common.buttons.continue')}
        sx={{ justifyContent: 'flex-end' }}
      />
    </Box>
  );
};

// ─── Step 2 form ──────────────────────────────────────────────────────────────

const Step2Form = ({
  onContinue,
  onBack,
  formData,
  updateFormData
}: StepFormProps) => {
  const { t } = useTranslation();
  const errorSummaryRef = useRef<HTMLDivElement>(null);
  const [errorSummaryItems, setErrorSummaryItems] = useState<string[]>([]);
  // Translates a validate() i18n key — returns '' when the key is empty (no error).
  const tv = (key: string) => (key ? t(key) : '');
  const [errors, setErrors] = useState<Record<string, string>>({
    companyName: '',
    companyAddress: '',
    companyWebsite: '',
    bizFirstName: '',
    bizLastName: '',
    bizEmail: '',
    bizPhone: '',
    adminFirstName: '',
    adminLastName: '',
    adminEmail: '',
    adminPhone: ''
  });
  const [validationSummary, setValidationSummary] = useState('');

  const companyNameRef = useRef<HTMLInputElement>(null);
  const companyAddressRef = useRef<HTMLInputElement>(null);
  const companyWebsiteRef = useRef<HTMLInputElement>(null);
  const bizFirstNameRef = useRef<HTMLInputElement>(null);
  const bizLastNameRef = useRef<HTMLInputElement>(null);
  const bizPhoneRef = useRef<HTMLInputElement>(null);
  const bizEmailRef = useRef<HTMLInputElement>(null);
  const adminFirstNameRef = useRef<HTMLInputElement>(null);
  const adminLastNameRef = useRef<HTMLInputElement>(null);
  const adminPhoneRef = useRef<HTMLInputElement>(null);
  const adminEmailRef = useRef<HTMLInputElement>(null);

  const setFieldError = (field: string, msg: string) =>
    setErrors((prev) => ({ ...prev, [field]: msg }));

  const handleContinue = () => {
    const newErrors: Record<string, string> = {
      companyName: !formData.companyName.trim()
        ? t('registrationStepsData.form.step2.companyName.errorRequired')
        : tv(validate('string', formData.companyName)),
      companyAddress: !formData.companyAddress.trim()
        ? t('registrationStepsData.form.step2.companyAddress.errorRequired')
        : tv(validate('string', formData.companyAddress)),
      companyWebsite: tv(validate('website', formData.companyWebsite)),
      bizFirstName: !formData.bizFirstName.trim()
        ? t(
          'registrationStepsData.form.step2.businessContact.firstName.errorRequired'
        )
        : tv(validate('string', formData.bizFirstName)),
      bizLastName: !formData.bizLastName.trim()
        ? t(
          'registrationStepsData.form.step2.businessContact.lastName.errorRequired'
        )
        : tv(validate('string', formData.bizLastName)),
      bizEmail: !formData.bizEmail.trim()
        ? t(
          'registrationStepsData.form.step2.businessContact.email.errorRequired'
        )
        : tv(validate('email', formData.bizEmail)),
      bizPhone: tv(validate('phone', formData.bizPhone)),
      adminFirstName: !formData.sameAsBusinessContact
        ? !formData.adminFirstName.trim()
          ? t(
            'registrationStepsData.form.step2.adminContact.firstName.errorRequired'
          )
          : tv(validate('string', formData.adminFirstName))
        : '',
      adminLastName: !formData.sameAsBusinessContact
        ? !formData.adminLastName.trim()
          ? t(
            'registrationStepsData.form.step2.adminContact.lastName.errorRequired'
          )
          : tv(validate('string', formData.adminLastName))
        : '',
      adminEmail: !formData.sameAsBusinessContact
        ? !formData.adminEmail.trim()
          ? t(
            'registrationStepsData.form.step2.adminContact.email.errorRequired'
          )
          : tv(validate('email', formData.adminEmail))
        : '',
      adminPhone: !formData.sameAsBusinessContact
        ? tv(validate('phone', formData.adminPhone))
        : ''
    };
    setErrors(newErrors);

    const summaryItems = Object.values(newErrors).filter(
      (value): value is string => Boolean(value)
    );
    setErrorSummaryItems(summaryItems);

    const focusOrder: Array<{ hasError: boolean; ref: React.RefObject<HTMLElement> }> = [
      { hasError: !!newErrors.companyName, ref: companyNameRef },
      { hasError: !!newErrors.companyAddress, ref: companyAddressRef },
      { hasError: !!newErrors.companyWebsite, ref: companyWebsiteRef },
      { hasError: !!newErrors.bizFirstName, ref: bizFirstNameRef },
      { hasError: !!newErrors.bizLastName, ref: bizLastNameRef },
      // visual order in the row: phone (left) then email (right)
      { hasError: !!newErrors.bizPhone, ref: bizPhoneRef },
      { hasError: !!newErrors.bizEmail, ref: bizEmailRef },
      // admin fields only mount when !sameAsBusinessContact; their errors are '' otherwise
      { hasError: !!newErrors.adminFirstName, ref: adminFirstNameRef },
      { hasError: !!newErrors.adminLastName, ref: adminLastNameRef },
      { hasError: !!newErrors.adminPhone, ref: adminPhoneRef },
      { hasError: !!newErrors.adminEmail, ref: adminEmailRef }
    ];

    const errorCount = Object.values(newErrors).filter(Boolean).length;
    if (errorCount > 0) {
      setValidationSummary(
        (errorCount === 1
          ? t('registrationStepsData.validation.summary.singular', {
            count: errorCount
          })
          : t('registrationStepsData.validation.summary.plural', {
            count: errorCount
          })) as string
      );

      const target = focusOrder.find((f) => f.hasError)?.ref;
      requestAnimationFrame(() => {
        errorSummaryRef.current?.focus();
      });
      setTimeout(() => {
        target?.current?.focus();
        target?.current?.scrollIntoView({
          block: 'start',
          behavior: 'smooth'
        });
      }, 100);

    } else {
      onContinue();
    }
  };

  return (
    <Box sx={{ ...cardSx }}>
      {/* Validation summary for screen readers */}
      <ScreenReaderAlert message={validationSummary} />
      <ErrorSummary
        messages={errorSummaryItems}
        summaryRef={errorSummaryRef}
      />
      {/* Company name */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 4 } }}>
        <CommonTextField
          required={true}
          label={t('registrationStepsData.form.step2.companyName.label')}
          autoComplete="organization"
          value={formData.companyName}
          onChange={(v) => {
            updateFormData({ companyName: v });
            setFieldError(
              'companyName',
              v.trim() ? tv(validate('string', v)) : ''
            );
          }}
          inputRef={companyNameRef}
          error={!!errors.companyName}
          errorMessage={errors.companyName}
        />
        <TwoColGrid>
        {/* Company address + website */}
        <CommonTextField
          required={true}
          label={t('registrationStepsData.form.step2.companyAddress.label')}
          autoComplete="street-address"
          value={formData.companyAddress}
          inputRef={companyAddressRef}
          onChange={(v) => {
            updateFormData({ companyAddress: v });
            setFieldError(
              'companyAddress',
              v.trim() ? tv(validate('string', v)) : ''
            );
          }}
          error={!!errors.companyAddress}
          errorMessage={errors.companyAddress}
          startIcon={
            <SvgIcon
              src="/images/registration/AddressIcon.svg"
              size={24}
              sx={fieldIconSx}
              color={errors.companyAddress ? '#9E231B' : '#1460AA'}
            />
          }
        />
        <CommonTextField
          label={t('registrationStepsData.form.step2.companyWebsite.label')}
          autoComplete="url"
          value={formData.companyWebsite}
          inputRef={companyWebsiteRef}
          onChange={(v) => {
            updateFormData({ companyWebsite: v });
            setFieldError('companyWebsite', tv(validate('website', v)));
          }}
          error={!!errors.companyWebsite}
          errorMessage={errors.companyWebsite}
          startIcon={
            <SvgIcon
              src="/images/registration/websiteIcon.svg"
              size={24}
              sx={fieldIconSx}
              color={errors.companyWebsite ? '#9E231B' : '#1460AA'}
            />
          }
        />
        </TwoColGrid>
      </Box>

      {/* Business contact details */}
      <Stack sx={{ gap: { xs: 4, md: 3 } }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 1 }
          }}
        >
          <SectionTitle>
            {t('registrationStepsData.form.step2.businessContact.sectionTitle')}
          </SectionTitle>
          <Typography sx={getDescriptionSx()}>
            {t('registrationStepsData.form.step2.businessContact.description')}
          </Typography>
        </Box>
        <TwoColGrid>
          <CommonTextField
            required={true}
            label={t(
              'registrationStepsData.form.step2.businessContact.firstName.label'
            )}
            autoComplete="given-name"
            inputRef={bizFirstNameRef}
            value={formData.bizFirstName}
            onChange={(v) => {
              updateFormData({ bizFirstName: v });
              setFieldError(
                'bizFirstName',
                v.trim() ? tv(validate('string', v)) : ''
              );
            }}
            error={!!errors.bizFirstName}
            errorMessage={errors.bizFirstName}
          />
          <CommonTextField
            required={true}
            label={t(
              'registrationStepsData.form.step2.businessContact.lastName.label'
            )}
            autoComplete="family-name"
            value={formData.bizLastName}
            inputRef={bizLastNameRef}
            onChange={(v) => {
              updateFormData({ bizLastName: v });
              setFieldError(
                'bizLastName',
                v.trim() ? tv(validate('string', v)) : ''
              );
            }}
            error={!!errors.bizLastName}
            errorMessage={errors.bizLastName}
          />
        </TwoColGrid>
        <TwoColGrid>
          <CommonTextField
            label={t(
              'registrationStepsData.form.step2.businessContact.phone.label'
            )}
            autoComplete="tel"
            value={formData.bizPhone}
            inputRef={bizPhoneRef}
            onChange={(v) => {
              updateFormData({ bizPhone: v });
              setFieldError('bizPhone', tv(validate('phone', v)));
            }}
            error={!!errors.bizPhone}
            errorMessage={errors.bizPhone}
            startIcon={
              <SvgIcon
                src="/images/registration/phoneIcon.svg"
                size={24}
                sx={fieldIconSx}
                color={errors.bizPhone ? '#9E231B' : '#1460AA'}
              />
            }
          />
          <CommonTextField
            required={true}
            label={t(
              'registrationStepsData.form.step2.businessContact.email.label'
            )}
            autoComplete="email"
            value={formData.bizEmail}
            inputRef={bizEmailRef}
            onChange={(v) => {
              updateFormData({ bizEmail: v });
              setFieldError(
                'bizEmail',
                v.trim() ? tv(validate('email', v)) : ''
              );
            }}
            error={!!errors.bizEmail}
            errorMessage={errors.bizEmail}
            startIcon={
              <SvgIcon
                src="/images/registration/emailIcon.svg"
                size={24}
                sx={fieldIconSx}
                color={errors.bizEmail ? '#9E231B' : '#1460AA'}
              />
            }
            helperText={t(
              'registrationStepsData.form.step2.businessContact.email.helperText'
            )}
          />
        </TwoColGrid>
      </Stack>

      {/* Admin contact details */}
      <Stack sx={{ gap: { xs: 4, md: 3 } }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 1 }
          }}
        >
          <SectionTitle>
            {t('registrationStepsData.form.step2.adminContact.sectionTitle')}
          </SectionTitle>
          <Typography sx={getDescriptionSx()}>
            {t('registrationStepsData.form.step2.adminContact.description')}
          </Typography>
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.sameAsBusinessContact}
              onChange={(e) =>
                updateFormData({ sameAsBusinessContact: e.target.checked })
              }
              sx={adminCheckboxSx}
            />
          }
          label={
            <Typography sx={getLabelSx({ fontSize: '13px' })}>
              {t(
                'registrationStepsData.form.step2.adminContact.sameAsBusinessCheckbox'
              )}
            </Typography>
          }
          sx={{ m: 0 }}
        />
        {!formData.sameAsBusinessContact && (
          <Stack sx={{ gap: '26px' }}>
            <TwoColGrid>
              <CommonTextField
                required={true}
                label={t(
                  'registrationStepsData.form.step2.adminContact.firstName.label'
                )}
                autoComplete="given-name"
                value={formData.adminFirstName}
                inputRef={adminFirstNameRef}
                onChange={(v) => {
                  updateFormData({ adminFirstName: v });
                  setFieldError(
                    'adminFirstName',
                    v.trim() ? tv(validate('string', v)) : ''
                  );
                }}
                error={!!errors.adminFirstName}
                errorMessage={errors.adminFirstName}
              />
              <CommonTextField
                required={true}
                label={t(
                  'registrationStepsData.form.step2.adminContact.lastName.label'
                )}
                autoComplete="family-name"
                value={formData.adminLastName}
                inputRef={adminLastNameRef}
                onChange={(v) => {
                  updateFormData({ adminLastName: v });
                  setFieldError(
                    'adminLastName',
                    v.trim() ? tv(validate('string', v)) : ''
                  );
                }}
                error={!!errors.adminLastName}
                errorMessage={errors.adminLastName}
              />
            </TwoColGrid>
            <TwoColGrid>
              <CommonTextField
                label={t(
                  'registrationStepsData.form.step2.adminContact.phone.label'
                )}
                autoComplete="tel"
                value={formData.adminPhone}
                inputRef={adminPhoneRef}
                onChange={(v) => {
                  updateFormData({ adminPhone: v });
                  setFieldError('adminPhone', tv(validate('phone', v)));
                }}
                error={!!errors.adminPhone}
                errorMessage={errors.adminPhone}
                startIcon={
                  <SvgIcon
                    src="/images/registration/phoneIcon.svg"
                    size={24}
                    sx={fieldIconSx}
                    color={errors.adminPhone ? '#9E231B' : '#1460AA'}
                  />
                }
              />
              <CommonTextField
                required={true}
                label={t(
                  'registrationStepsData.form.step2.adminContact.email.label'
                )}
                autoComplete="email"
                value={formData.adminEmail}
                inputRef={adminEmailRef}
                onChange={(v) => {
                  updateFormData({ adminEmail: v });
                  setFieldError(
                    'adminEmail',
                    v.trim() ? tv(validate('email', v)) : ''
                  );
                }}
                error={!!errors.adminEmail}
                errorMessage={errors.adminEmail}
                startIcon={
                  <SvgIcon
                    src="/images/registration/emailIcon.svg"
                    size={24}
                    sx={fieldIconSx}
                    color={errors.adminEmail ? '#9E231B' : '#1460AA'}
                  />
                }
                helperText={t(
                  'registrationStepsData.form.step2.adminContact.email.helperText'
                )}
              />
            </TwoColGrid>
          </Stack>
        )}
      </Stack>

      {/* Footer */}
      <CommonFooter
        onBack={onBack}
        onSubmit={handleContinue}
        submitText={t('registrationStepsData.common.buttons.continue')}
      />
    </Box>
  );
};

// ─── Step 3 form ──────────────────────────────────────────────────────────────

const Step3Form = ({
  onContinue,
  onBack,
  formData,
  updateFormData,
  state,
  language
}: StepFormProps) => {
  const { t } = useTranslation();
  // Translates a validate() i18n key — returns '' when the key is empty (no error).
  const tv = (key: string) => (key ? t(key) : '');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerHeight, setPickerHeight] = useState(0);
  const cardRef = useRef<HTMLDivElement>(null);

  const [errors, setErrors] = useState<Record<string, string>>({
    flow: '',
    ndcIntegration: '',
    ndcSpecify: ''
  });

  const flowRef = useRef<HTMLDivElement>(null);        // CommonSelect trigger
  const ndcIntegrationRef = useRef<HTMLInputElement>(null); // first radio input
  const ndcSpecifyRef = useRef<HTMLInputElement>(null);

  const [validationSummary, setValidationSummary] = useState('');

  const handlePickerOpenChange = useCallback((isOpen: boolean) => {
    if (!isOpen) {
      setPickerOpen(false);
      setPickerHeight(0);
      return;
    }
    setPickerOpen(true);
  }, []);

  useEffect(() => {
    if (!pickerOpen) return;
    const popup = cardRef.current?.querySelector(
      '.day-picker'
    ) as HTMLElement | null;
    if (!popup || !cardRef.current) return;
    const cardRect = cardRef.current.getBoundingClientRect();
    const popupBottom = popup.getBoundingClientRect().bottom;
    const overflow = popupBottom - cardRect.bottom;
    setPickerHeight(
      overflow > 0 ? Math.ceil(cardRect.height + overflow + 16) : 0
    );
  }, [pickerOpen]);

  const handleContinue = () => {
    const newErrors: Record<string, string> = {
      flow: !formData.flow
        ? t('registrationStepsData.form.step3.flow.error')
        : '',
      ndcIntegration: !formData.ndcIntegration
        ? t('registrationStepsData.form.step3.ndcIntegration.error')
        : '',
      ndcSpecify: tv(validate('string', formData.ndcSpecify))
    };
    setErrors(newErrors);

    const focusOrder: Array<{ hasError: boolean; ref: React.RefObject<HTMLElement> }> = [
      { hasError: !!newErrors.flow, ref: flowRef },
      { hasError: !!newErrors.ndcIntegration, ref: ndcIntegrationRef },
      { hasError: !!newErrors.ndcSpecify, ref: ndcSpecifyRef }
    ];

    const errorCount = Object.values(newErrors).filter(Boolean).length;
    if (errorCount > 0) {
      setValidationSummary(
        (errorCount === 1
          ? t('registrationStepsData.validation.summary.singular', {
            count: errorCount
          })
          : t('registrationStepsData.validation.summary.plural', {
            count: errorCount
          })) as string
      );

      const target = focusOrder.find((f) => f.hasError)?.ref;
      setTimeout(() => {
        target?.current?.focus();
        target?.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }, 100);
    } else {
      onContinue();
    }
  };

  return (
    <Box
      ref={cardRef}
      className="step3-form"
      sx={{
        ...cardSx,
        minHeight: pickerHeight > 0 ? `${pickerHeight}px` : 'auto'
      }}
    >
      {/* Validation summary for screen readers */}
      <ScreenReaderAlert message={validationSummary} />
      {/* When to begin + What flow */}
      <CommonDatePicker
        label={t('registrationStepsData.form.step3.implDate.label')}
        value={formData.implDate}
        onChange={(date) => updateFormData({ implDate: date })}
        helperText={t(
          'registrationStepsData.form.step3.implDate.helperText'
        )}
        onOpenChange={handlePickerOpenChange}
      />
      {/* What flow */}

      <CommonSelect
        value={formData.flow}
        triggerRef={flowRef}
        onChange={(v) => {
          updateFormData({ flow: v });
          setErrors((prev) => ({ ...prev, flow: '' }));
        }}
        label={t('registrationStepsData.form.step3.flow.label')}
        menuList={sortDropdownOptions(
          state.supportedFlow?.[language || 'en']
        )}
        error={!!errors.flow}
        errorMessage={errors.flow}
        required
      />
   
      {/* NDC integration */}
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <CommonRadioQuestion
            name="ndcIntegration"
            question={t(
              'registrationStepsData.form.step3.ndcIntegration.label'
            )}
            value={formData.ndcIntegration}
            inputRef={ndcIntegrationRef}
            onChange={(v) => {
              updateFormData({
                ndcIntegration: v,
                ndcSpecify: v === 'Yes' ? formData.ndcSpecify : ''
              });
              setErrors((prev) => ({
                ...prev,
                ndcIntegration: '',
                ndcSpecify: ''
              }));
            }}
            optionLabels={[
              t('registrationStepsData.common.yes'),
              t('registrationStepsData.common.no')
            ]}
            error={!!errors.ndcIntegration}
            errorMessage={errors.ndcIntegration}
          />
        </Grid>
        {formData.ndcIntegration === 'Yes' && (
          <Grid item xs={12} sm={8}>
            <CommonTextField
              label={t(
                'registrationStepsData.form.step3.ndcIntegration.specifyLabel'
              )}
              value={formData.ndcSpecify}
              inputRef={ndcSpecifyRef}
              onChange={(v) => {
                updateFormData({ ndcSpecify: v });
                setErrors((prev) => ({
                  ...prev,
                  ndcSpecify: v.trim() ? tv(validate('string', v)) : ''
                }));
              }}
              error={!!errors.ndcSpecify}
              errorMessage={errors.ndcSpecify}
            />
          </Grid>
        )}
      </Grid>

      {/* Footer */}
      <CommonFooter
        onBack={onBack}
        onSubmit={handleContinue}
        submitText={t('registrationStepsData.common.buttons.continue')}
      />
    </Box>
  );
};

// ─── Step 4 form (Review & submit) ────────────────────────────────────────────

const Step4Form = ({
  onBack,
  onContinue,
  formData,
  state,
  language
}: StepFormProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    isSubmitting,
    submitSuccess: submitted,
    submitError
  } = useSelector(selectSubmitStatus);
  const [dialogOpen, setDialogOpen] = useState(false);
  const payloadRef = useRef<any>(null);
  const isErrorState = !!submitError && submitError.ErrorCode === 409;

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
  }, []);

  // Close dialog when the API call succeeds
  useEffect(() => {
    if (submitted) {
      setDialogOpen(false);
    }
  }, [submitted]);

  const buildAndOpenDialog = () => {
    const lang = language || 'en';

    const getCode = (arr: any[] | undefined, desc: string): string => {
      const found = (arr ?? []).find((item: any) => item.desc === desc);
      return found?.code ?? desc;
    };

    const adminFirstName = formData.sameAsBusinessContact
      ? formData.bizFirstName
      : formData.adminFirstName;
    const adminLastName = formData.sameAsBusinessContact
      ? formData.bizLastName
      : formData.adminLastName;
    const adminEmail = formData.sameAsBusinessContact
      ? formData.bizEmail
      : formData.adminEmail;
    const adminPhone = formData.sameAsBusinessContact
      ? formData.bizPhone
      : formData.adminPhone;

    const customerTypeCode = getCode(
      state.customerTypes?.[lang],
      formData.customers
    );
    const bizModelCodes = (formData.bizModel ?? []).map((desc) =>
      getCode(state.businessModel?.[lang], desc)
    );
    const apiSchemaCodes = (formData.apiSchema ?? []).map((desc) =>
      getCode(state.apiSchemas?.[lang], desc)
    );
    const flowCode = getCode(state.supportedFlow?.[lang], formData.flow);

    payloadRef.current = {
      company_name: formData.companyName,
      company_address: formData.companyAddress,
      company_website: formData.companyWebsite,

      admin_first_name: adminFirstName,
      admin_last_name: adminLastName,
      admin_email: adminEmail,
      admin_phone_no: adminPhone,

      business_contact_first_name: formData.bizFirstName,
      business_contact_last_name: formData.bizLastName,
      business_email: formData.bizEmail,
      business_phone_no: formData.bizPhone,

      customer_type: customerTypeCode,
      business_model_type: bizModelCodes.map((code) => ({
        id: code,
        iata: null
      })),
      pos_preference: (formData.appliedPOS ?? []).map((item) => ({
        id: item.code,
        iata: item.iata ? item.iata : null
      })),
      api_schema_version: apiSchemaCodes,
      implementation_start_date: formData.implDate
        ? formData.implDate.toISOString().split('T')[0]
        : null,
      support_flow: [flowCode],
      existing_integration: formData.ndcIntegration === 'Yes',
      existing_integration_comments: formData.ndcSpecify || '',
      supported_by_IT: formData.itTeam === 'Yes'
    };
    setDialogOpen(true);
  };

  return (
    <Box sx={{ ...cardSx, gap: { xs: 4, md: 5 } }}>
      {/* ── Integration context ── */}
      <Stack gap={3}>
        <SectionTitle>
          {t(
            'registrationStepsData.form.step4.integrationContext.sectionTitle'
          )}
        </SectionTitle>
        <ReviewRow
          label={t(
            'registrationStepsData.form.step4.businessModel.customers'
          )}
          value={
            formData.customers ||
            t('registrationStepsData.common.fallbackEmpty')
          }
        />
        <ReviewRow
          label={t('registrationStepsData.form.step4.businessModel.model')}
          value={
            formData.bizModel.length > 0
              ? formData.bizModel.join(', ')
              : t('registrationStepsData.common.fallbackEmpty')
          }
        />
        <ReviewRow
          label={t(
            'registrationStepsData.form.step4.integrationContext.itTeam'
          )}
          value={
            formData.itTeam === 'Yes'
              ? t('registrationStepsData.common.yes')
              : formData.itTeam === 'No'
                ? t('registrationStepsData.common.no')
                : t('registrationStepsData.common.fallbackEmpty')
          }
        />
        <ReviewRow
          label={t(
            'registrationStepsData.form.step4.integrationContext.apiSchema'
          )}
          value={
            formData.apiSchema?.length > 0
              ? formData.apiSchema.join(', ')
              : t('registrationStepsData.common.fallbackEmpty')
          }
        />
        <Stack gap={1}>
          <Typography sx={step4PosLabelSx}>
            {t('registrationStepsData.form.step4.integrationContext.pos')}
          </Typography>
          <Stack direction="row" sx={step4PosStackSx}>
            {formData.appliedPOS && formData.appliedPOS.length > 0 ? (
              formData.appliedPOS.map((pos) => (
                <Stack
                  key={pos.code}
                  direction="row"
                  alignItems="center"
                  gap={1}
                >
                  <PosCountryItem
                    flag={pos.flag}
                    name={pos.code === 'OT' ? t('registrationStepsData.modal.othersRegion.formLabel') : pos.name}
                    iata={pos.iata ? `${pos.iata}` : undefined}
                  />
                </Stack>
              ))
            ) : (
              <Typography aria-label={`${t('registrationStepsData.common.fallbackEmptyAriaLabel')}`} sx={fallbackTextSx}>
                {t('registrationStepsData.common.fallbackEmpty')}
              </Typography>
            )}
          </Stack>
        </Stack>
      </Stack>

      {/* ── Commercial details ── */}
      <Stack gap={5}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <SectionTitle>
            {t(
              'registrationStepsData.form.step4.commercialDetails.sectionTitle'
            )}
          </SectionTitle>

          <ReviewRow
            label={t(
              'registrationStepsData.form.step4.commercialDetails.companyName'
            )}
            value={
              formData.companyName ||
              t('registrationStepsData.common.fallbackEmpty')
            }
          />
          <TwoColGrid>
            <ReviewIconRow
              src="/images/registration/AddressIcon.svg"
              alt=""
              label={t(
                'registrationStepsData.form.step4.commercialDetails.companyAddress'
              )}
              value={
                formData.companyAddress ||
                t('registrationStepsData.common.fallbackEmpty')
              }
            />
            <ReviewIconRow
              src="/images/registration/websiteIcon.svg"
              alt=""
              label={t(
                'registrationStepsData.form.step4.commercialDetails.companyWebsite'
              )}
              value={
                formData.companyWebsite ||
                t('registrationStepsData.common.fallbackEmpty')
              }
            />
          </TwoColGrid>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <SectionTitle>
            {t('registrationStepsData.form.step4.businessContact.sectionTitle')}
          </SectionTitle>
          <TwoColGrid>
            <ReviewRow
              label={t(
                'registrationStepsData.form.step4.businessContact.firstName'
              )}
              value={
                formData.bizFirstName ||
                t('registrationStepsData.common.fallbackEmpty')
              }
            />
            <ReviewRow
              label={t(
                'registrationStepsData.form.step4.businessContact.lastName'
              )}
              value={
                formData.bizLastName ||
                t('registrationStepsData.common.fallbackEmpty')
              }
            />
          </TwoColGrid>
          <TwoColGrid>
            <ReviewIconRow
              src="/images/registration/phoneIcon.svg"
              alt=""
              label={t(
                'registrationStepsData.form.step4.businessContact.phone'
              )}
              value={
                formData.bizPhone ||
                t('registrationStepsData.common.fallbackEmpty')
              }
            />
            <ReviewIconRow
              src="/images/registration/emailIcon.svg"
              alt=""
              label={t(
                'registrationStepsData.form.step4.businessContact.email'
              )}
              value={
                formData.bizEmail ||
                t('registrationStepsData.common.fallbackEmpty')
              }
            />
          </TwoColGrid>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <SectionTitle>
            {t('registrationStepsData.form.step4.adminContact.sectionTitle')}
          </SectionTitle>
          <TwoColGrid>
            <ReviewRow
              label={t(
                'registrationStepsData.form.step4.adminContact.firstName'
              )}
              value={
                formData.sameAsBusinessContact
                  ? formData.bizFirstName
                  : formData.adminFirstName ||
                  t('registrationStepsData.common.fallbackEmpty')
              }
            />
            <ReviewRow
              label={t(
                'registrationStepsData.form.step4.adminContact.lastName'
              )}
              value={
                formData.sameAsBusinessContact
                  ? formData.bizLastName
                  : formData.adminLastName ||
                  t('registrationStepsData.common.fallbackEmpty')
              }
            />
          </TwoColGrid>
          <TwoColGrid>
            <ReviewIconRow
              src="/images/registration/phoneIcon.svg"
              alt=""
              label={t('registrationStepsData.form.step4.adminContact.phone')}
              value={
                formData.sameAsBusinessContact
                                    ? formData.bizPhone || t('registrationStepsData.common.fallbackEmpty')

                  : formData.adminPhone ||
                  t('registrationStepsData.common.fallbackEmpty')
              }
            />
            <ReviewIconRow
              src="/images/registration/emailIcon.svg"
              alt=""
              label={t('registrationStepsData.form.step4.adminContact.email')}
              value={
                formData.sameAsBusinessContact
                  ? formData.bizEmail
                  : formData.adminEmail ||
                  t('registrationStepsData.common.fallbackEmpty')
              }
            />
          </TwoColGrid>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 3, sm: 4, md: 3 }
          }}
        >
        </Box>
      </Stack>

      {/* ── Technical details ── */}
      <Stack gap={{ xs: 3, sm: 4, md: 3 }}>
        <SectionTitle>
          {t('registrationStepsData.form.step4.technicalDetails.sectionTitle')}
        </SectionTitle>
        <Stack direction="row" alignItems="flex-start" spacing={1}>
          <ReviewIconRow
            src="/images/registration/Calendar.svg"
            alt=""
            label={t(
              'registrationStepsData.form.step4.technicalDetails.implDate'
            )}
            value={
              formData.implDate
                ? formData.implDate.toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })
                : t('registrationStepsData.common.fallbackEmpty')
            }
          />
        </Stack>
        <ReviewRow
          label={t('registrationStepsData.form.step4.technicalDetails.flow')}
          value={
            formData.flow || t('registrationStepsData.common.fallbackEmpty')
          }
        />
        <ReviewRow
          label={t(
            'registrationStepsData.form.step4.technicalDetails.ndcIntegration'
          )}
          value={
            formData.ndcIntegration === 'Yes'
              ? t('registrationStepsData.common.yes')
              : formData.ndcIntegration === 'No'
                ? t('registrationStepsData.common.no')
                : t('registrationStepsData.common.fallbackEmpty')
          }
        />
        {formData.ndcIntegration === 'Yes' && formData.ndcSpecify && (
          <ReviewRow label="" value={formData.ndcSpecify} />
        )}
      </Stack>

      {/* Footer */}
      <CommonFooter
        onBack={onBack}
        onSubmit={buildAndOpenDialog}
        submitText={t('registrationStepsData.common.buttons.submit')}
      />
      <Box
        aria-live="assertive"
        aria-atomic="true"
        sx={screenReaderAlertSx}
      >
        {isSubmitting
          ? t('registrationStepsData.form.step4.submitStatus.submitting')
          : submitted
            ? t('registrationStepsData.form.step4.submitStatus.success')
              : ''}
      </Box>
      <SellerRegDialog
        open={dialogOpen}
        variant={isErrorState ? 'alert' : 'confirm'}
        isSubmitting={isSubmitting}
        payload={payloadRef.current}
        title={
          isErrorState
            ? t('AlertPopUpRegistration.header')
            : t('registrationStepsData.dialog.title')
        }
        body={
          isErrorState
            ? t('AlertPopUpRegistration.data')
            : t('registrationStepsData.dialog.body')
        }
        options={[
          t('registrationStepsData.dialog.options.submit'),
          t('registrationStepsData.dialog.options.redirect')
        ]}
        back={t('registrationStepsData.common.buttons.returnToForm')}
        confirm={t('registrationStepsData.common.buttons.confirm')}
        handleOnClose={
          isErrorState
            ? () => {
              setDialogOpen(false);
              dispatch(resetSubmitError());
            }
            : handleDialogClose
        }
        navigateURL={t('registrationStepsData.connectionOptionsUrl')}
      />
    </Box>
  );
};

// ─── Redux selectors (defined outside component to avoid re-creation on every render) ────

const selectSellerReg = (state: any) => state.sellerReg;

const selectSellerRegData = createSelector(
  [selectSellerReg],
  ({ pos, businessModel, customerTypes, apiSchemas, supportedFlow }) => ({
    pos,
    businessModel,
    customerTypes,
    apiSchemas,
    supportedFlow
  })
);

const selectSubmitStatus = createSelector(
  [selectSellerReg],
  ({ isSubmitting, submitSuccess, submitError }) => ({
    isSubmitting,
    submitSuccess,
    submitError
  })
);

// ─── Main component ───────────────────────────────────────────────────────────

const SellerRegistrationForm = ({
  stepNumber,
  onContinue,
  onBack,
  // scrollOffset ,
  formData,
  updateFormData,
  onOtherSelected
}: SellerRegistrationFormProps) => {
  const state = useSelector(selectSellerRegData);
  const { i18n } = useTranslation();
  const language = i18n.language;
  const isMobile = useMediaQuery('(max-width:600px)');

  const scrollOffset = isMobile ? 150 : 200;
  return (
    <div id="seller-registration-form-root" tabIndex={-1}>
      {(() => {
        switch (stepNumber) {
          case 1:
            return (
              <Step1Form
                onContinue={onContinue}
                onBack={onBack}
                formData={formData}
                updateFormData={updateFormData}
                state={state}
                language={language}
                onOtherSelected={onOtherSelected}
              />
            );
          case 2:
            return (
              <Step2Form
                onContinue={onContinue}
                onBack={onBack}
                formData={formData}
                updateFormData={updateFormData}
                state={state}
                language={language}
              />
            );
          case 3:
            return (
              <Step3Form
                onContinue={onContinue}
                onBack={onBack}
                formData={formData}
                updateFormData={updateFormData}
                state={state}
                language={language}
              />
            );
          case 4:
            return (
              <Step4Form
                onContinue={onContinue}
                onBack={onBack}
                formData={formData}
                updateFormData={updateFormData}
                state={state}
                language={language}
              />
            );
          default:
            return null;
        }
      })()}
    </div>
  );
};

export { CommonFooter, SvgIcon, FieldError, CommonSelect, CommonMultiSelect, CommonDatePicker };
export default SellerRegistrationForm;
